/**
 * Public buyer self-signup flow (marketplace transactional layer).
 *
 * Four steps, mirroring the farmer /signup wizard (signup.tsx) but against the
 * dedicated buyer endpoints + the OTP purpose 'buyer_signup':
 *   0. enter phone        -> POST /auth/otp/request {purpose:'buyer_signup'}
 *   1. enter 5-digit OTP  -> POST /auth/otp/verify  {purpose:'buyer_signup'}
 *   2. read + accept POPIA consent (GET /consent/current; no API call to advance)
 *   3. enter profile + PIN-> POST /buyers/signup     -> {access_token, ...}
 *
 * On success the server mints tokens (same shape login returns) + the new user
 * id/role; we persist the tokens exactly like login.tsx/signup.tsx and route by
 * role (a fresh buyer lands on /marketplace).
 *
 * OTP + signup are hand-rolled on the shared mutator (src/api/buyers.ts) because
 * orval isn't regenerated for /buyers and the generated OTP purpose enum lacks
 * 'buyer_signup'. Consent reuses the generated /consent client.
 *
 * Copy goes through i18next with English defaultValues (TD-088) so the screen
 * renders correctly before the buyerSignup.* keys are added to the locales; the
 * keys are listed in the build manifest for the central process to wire in.
 */
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  OtpInput,
  PhoneInput,
  Select,
  Stack,
  Stepper,
  Text,
  TextField,
  Typography,
} from '@fse/ui';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { consentControllerGetCurrent } from '../api/generated/consent/consent';
import type { ConsentVersionDto } from '../api/generated/models';
import {
  buyerSignup,
  requestBuyerSignupOtp,
  verifyBuyerSignupOtp,
  type AuthTokensResponse,
  type BuyerPreferredLang,
} from '../api/buyers';

function buildSchemas(t: TFunction) {
  // Phone is captured as the 9-digit ZA-local portion (PhoneInput renders the
  // fixed `+27` adornment); E.164 is rebuilt at the API boundary.
  const phoneSchema = z.object({
    phone: z.string().regex(/^\d{9}$/, t('buyerSignup.validation.phone', 'Enter a valid 9-digit phone number')),
  });
  const otpSchema = z.object({
    code: z.string().regex(/^\d{5}$/, t('buyerSignup.validation.otp', 'Enter the 5-digit code')),
  });
  const profileSchema = z
    .object({
      displayName: z
        .string()
        .trim()
        .min(1, t('buyerSignup.validation.displayNameRequired', 'Enter your name or business name'))
        .max(200, t('buyerSignup.validation.displayNameTooLong', 'That name is too long')),
      pin: z.string().regex(/^\d{4,8}$/, t('buyerSignup.validation.pin', 'PIN must be 4 to 8 digits')),
      confirmPin: z.string(),
    })
    .refine((v) => v.pin === v.confirmPin, {
      path: ['confirmPin'],
      message: t('buyerSignup.validation.pinMismatch', 'PINs do not match'),
    });
  return { phoneSchema, otpSchema, profileSchema };
}

const SUPPORTED_LANGUAGES: readonly BuyerPreferredLang[] = ['en', 'xi', 'nso', 'ts'];

// Language self-labels (endonyms) — intentionally NOT translated.
const LANGUAGE_LABELS: Record<BuyerPreferredLang, string> = {
  en: 'English',
  xi: 'isiXhosa',
  nso: 'Sepedi',
  ts: 'Xitsonga',
};

function persistTokens(tokens: AuthTokensResponse) {
  window.localStorage.setItem('fse_access_token', tokens.access_token);
  window.localStorage.setItem('fse_refresh_token', tokens.refresh_token);
}

export function BuyerSignupScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const STEPS = [
    t('buyerSignup.step.phone', 'Phone'),
    t('buyerSignup.step.verify', 'Verify'),
    t('buyerSignup.step.terms', 'Terms'),
    t('buyerSignup.step.profile', 'Profile'),
  ] as const;

  const { phoneSchema, otpSchema, profileSchema } = useMemo(() => buildSchemas(t), [t]);

  const [step, setStep] = useState<0 | 1 | 2 | 3>(0);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [preferredLang, setPreferredLang] = useState<BuyerPreferredLang>('en');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const [consent, setConsent] = useState<ConsentVersionDto | null>(null);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentReload, setConsentReload] = useState(0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<{
    phone?: string;
    code?: string;
    displayName?: string;
    pin?: string;
    confirmPin?: string;
  }>({});
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (step !== 2) return;
    const controller = new AbortController();
    setConsentLoading(true);
    setConsentError(null);
    consentControllerGetCurrent(controller.signal)
      .then((dto) => setConsent(dto))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setConsent(null);
        setConsentError(
          err instanceof FseApiError
            ? err.message
            : t('buyerSignup.consent.loadError', 'Could not load the terms. Please try again.'),
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setConsentLoading(false);
      });
    return () => controller.abort();
  }, [step, consentReload, t]);

  function mapApiError(err: unknown, kind: 'request' | 'verify' | 'signup'): string {
    if (err instanceof FseApiError) {
      if (err.status === 409) {
        return t('buyerSignup.apiError.conflict', 'An account with this phone number already exists. Try signing in.');
      }
      if (err.status === 404) {
        return t('buyerSignup.apiError.notFound', 'We could not find a matching verification. Start again.');
      }
      if (err.status === 401) {
        return kind === 'signup'
          ? t('buyerSignup.apiError.expired', 'Your verification expired. Please start again.')
          : t('buyerSignup.apiError.badCode', 'That code is incorrect or has expired.');
      }
      if (err.status === 400) {
        return t('buyerSignup.apiError.badRequest', 'Please check your details and try again.');
      }
      return err.message;
    }
    return String(err);
  }

  async function onSubmitPhone(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = phoneSchema.safeParse({ phone });
    if (!parsed.success) {
      setFieldErrors({ phone: parsed.error.issues[0]?.message });
      return;
    }
    setPending(true);
    try {
      await requestBuyerSignupOtp(`+27${parsed.data.phone}`);
      setStep(1);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'request'));
    } finally {
      setPending(false);
    }
  }

  async function onSubmitOtp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = otpSchema.safeParse({ code });
    if (!parsed.success) {
      setFieldErrors({ code: parsed.error.issues[0]?.message });
      return;
    }
    setPending(true);
    try {
      await verifyBuyerSignupOtp(`+27${phone}`, parsed.data.code);
      setStep(2);
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'verify'));
    } finally {
      setPending(false);
    }
  }

  function onAcceptConsent() {
    if (!consent) return;
    setApiErrorMessage(null);
    setStep(3);
  }

  const consentBoxRef = useCallback((node: HTMLDivElement | null) => {
    if (node && node.scrollHeight <= node.clientHeight + 8) {
      setScrolledToBottom(true);
    }
  }, []);

  async function onSubmitProfile(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setApiErrorMessage(null);
    const parsed = profileSchema.safeParse({ displayName, pin, confirmPin });
    if (!parsed.success) {
      const errs: { displayName?: string; pin?: string; confirmPin?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'displayName' || key === 'pin' || key === 'confirmPin') {
          errs[key] = issue.message;
        }
      }
      setFieldErrors(errs);
      return;
    }
    if (!consent) {
      setApiErrorMessage(t('buyerSignup.apiError.expired', 'Your verification expired. Please start again.'));
      return;
    }
    setPending(true);
    try {
      const tokens = await buyerSignup({
        phone: `+27${phone}`,
        pin: parsed.data.pin,
        displayName: parsed.data.displayName,
        preferredLang,
        consentVersionId: consent.id,
      });
      persistTokens(tokens);
      // A fresh buyer lands on the marketplace browse. (Role is whatever the
      // server minted — the _app gate + index redirect handle any edge role.)
      void navigate({ to: '/marketplace' });
    } catch (err) {
      setApiErrorMessage(mapApiError(err, 'signup'));
    } finally {
      setPending(false);
    }
  }

  function onLangChange(event: { target: { value: string } }) {
    setPreferredLang(event.target.value as BuyerPreferredLang);
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ position: 'fixed', top: 8, right: 8 }}>
        <ColorModeToggle />
      </Box>
      <Container maxWidth="xs">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 1 }}>
              <Text variant="display-small" as="h1" align="center">
                {t('buyerSignup.heading', 'Buy on the Exchange')}
              </Text>
            </Box>
            <Text variant="body-large" align="center" color="text.secondary">
              {t('buyerSignup.tagline', 'Create a buyer account to browse listings and make offers.')}
            </Text>
          </Box>

          <Stepper steps={[...STEPS]} activeStep={step} />

          <Card>
            <CardContent>
              {step === 0 && (
                <form onSubmit={onSubmitPhone} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('buyerSignup.phone.body', 'Enter your mobile number. We will send a one-time code to verify it.')}
                    </Typography>
                    <PhoneInput
                      label={t('buyerSignup.phone.label', 'Mobile number')}
                      value={phone}
                      onChangeText={setPhone}
                      error={fieldErrors.phone}
                      disabled={pending}
                    />
                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}
                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending
                        ? t('buyerSignup.phone.sending', 'Sending…')
                        : t('buyerSignup.phone.send', 'Send code')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 1 && (
                <form onSubmit={onSubmitOtp} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('buyerSignup.verify.body', { phone: `+27${phone}`, defaultValue: 'Enter the 5-digit code we sent to {{phone}}.' })}
                    </Typography>
                    <Box>
                      <OtpInput
                        value={code}
                        onChange={setCode}
                        length={5}
                        error={!!fieldErrors.code}
                        disabled={pending}
                        aria-label={t('buyerSignup.verify.label', 'Verification code')}
                      />
                      {fieldErrors.code && (
                        <Box sx={{ mt: 1 }}>
                          <Text variant="body-small" color="error.main">
                            {fieldErrors.code}
                          </Text>
                        </Box>
                      )}
                    </Box>
                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}
                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending
                        ? t('buyerSignup.verify.verifying', 'Verifying…')
                        : t('buyerSignup.verify.verify', 'Verify')}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={pending}
                      onClick={() => {
                        setApiErrorMessage(null);
                        setFieldErrors({});
                        setCode('');
                        setStep(0);
                      }}
                    >
                      {t('buyerSignup.verify.useDifferent', 'Use a different number')}
                    </Button>
                  </Stack>
                </form>
              )}

              {step === 2 && (
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('buyerSignup.terms.intro', 'Please read and accept the terms to continue.')}
                  </Typography>

                  {consentLoading && (
                    <Typography variant="body2" color="text.secondary">
                      {t('buyerSignup.terms.loading', 'Loading terms…')}
                    </Typography>
                  )}

                  {consentError && (
                    <Alert
                      severity="error"
                      variant="outlined"
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => setConsentReload((n) => n + 1)}
                        >
                          {t('buyerSignup.terms.retry', 'Retry')}
                        </Button>
                      }
                    >
                      {consentError}
                    </Alert>
                  )}

                  {consent && !consentLoading && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {t('buyerSignup.terms.version', {
                          version: consent.version,
                          effective: consent.effective_from,
                          defaultValue: 'Version {{version}} · effective {{effective}}',
                        })}
                      </Typography>
                      <Box
                        ref={consentBoxRef}
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
                            setScrolledToBottom(true);
                          }
                        }}
                        sx={{
                          maxHeight: 280,
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          fontSize: 14,
                        }}
                      >
                        {consent.body_md}
                      </Box>
                      {!scrolledToBottom && (
                        <Typography variant="caption" color="text.secondary">
                          {t('buyerSignup.terms.scrollHint', 'Scroll to the bottom to continue.')}
                        </Typography>
                      )}
                      {apiErrorMessage && (
                        <Alert severity="error" variant="outlined">
                          {apiErrorMessage}
                        </Alert>
                      )}
                      <Button
                        size="large"
                        fullWidth
                        disabled={pending || !scrolledToBottom}
                        onClick={onAcceptConsent}
                      >
                        {t('buyerSignup.terms.accept', 'Accept and continue')}
                      </Button>
                    </>
                  )}
                </Stack>
              )}

              {step === 3 && (
                <form onSubmit={onSubmitProfile} noValidate>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('buyerSignup.profile.body', 'Tell us who you are and set a PIN to secure your account.')}
                    </Typography>
                    <TextField
                      label={t('buyerSignup.profile.displayName', 'Name or business name')}
                      autoComplete="name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      slotProps={{ htmlInput: { maxLength: 200 } }}
                      error={!!fieldErrors.displayName}
                      helperText={fieldErrors.displayName}
                      disabled={pending}
                    />

                    <FormControl fullWidth disabled={pending}>
                      <InputLabel id="buyer-language-label">
                        {t('buyerSignup.profile.preferredLang', 'Preferred language')}
                      </InputLabel>
                      <Select
                        labelId="buyer-language-label"
                        id="buyer-language"
                        value={preferredLang}
                        label={t('buyerSignup.profile.preferredLang', 'Preferred language')}
                        onChange={onLangChange}
                      >
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <MenuItem key={lang} value={lang}>
                            {LANGUAGE_LABELS[lang]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label={t('buyerSignup.profile.pin', 'PIN')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      error={!!fieldErrors.pin}
                      helperText={fieldErrors.pin ?? t('buyerSignup.profile.pinHint', '4 to 8 digits')}
                      disabled={pending}
                    />
                    <TextField
                      label={t('buyerSignup.profile.confirmPin', 'Confirm PIN')}
                      type="password"
                      autoComplete="new-password"
                      inputMode="numeric"
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value)}
                      error={!!fieldErrors.confirmPin}
                      helperText={fieldErrors.confirmPin}
                      disabled={pending}
                    />

                    {apiErrorMessage && (
                      <Alert severity="error" variant="outlined">
                        {apiErrorMessage}
                      </Alert>
                    )}

                    <Button type="submit" size="large" fullWidth disabled={pending}>
                      {pending
                        ? t('buyerSignup.profile.creating', 'Creating account…')
                        : t('buyerSignup.profile.create', 'Create buyer account')}
                    </Button>
                  </Stack>
                </form>
              )}
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                {t('buyerSignup.footer.backToSignIn', 'Already have an account? Sign in')}
              </Typography>
            </Link>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
